import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Cpu, HardDrive, Database, Activity, AlertTriangle, CheckCircle,
  RefreshCw, Server, Layers, TrendingUp, Zap,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

/* ────────────────────────────────────────────────────────────────────────── */
/* Capacity Insights Panel — SuperAdmin                                        */
/*                                                                            */
/* Tells the operator how loaded the host + Mongo are right now and when it   */
/* is time to upgrade the VPS. Polls /api/superadmin/capacity every 20s.      */
/* ────────────────────────────────────────────────────────────────────────── */

const STATUS_META = {
  healthy:      { label: 'Healthy',         color: 'emerald', icon: CheckCircle },
  plan_upgrade: { label: 'Plan upgrade',    color: 'amber',   icon: TrendingUp },
  upgrade_now:  { label: 'Upgrade now',     color: 'red',     icon: AlertTriangle },
};

function Gauge({ pct, label, sub, color }) {
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  const ring =
    clamped >= 85 ? '#ef4444' :
    clamped >= 70 ? '#f59e0b' :
    clamped >= 50 ? '#facc15' : '#10b981';
  const c = color || ring;
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-slate-400 text-xs font-medium uppercase tracking-wider">{label}</span>
        <span className="text-xs text-slate-500">{sub}</span>
      </div>
      <div className="flex items-end gap-2 mb-2">
        <div className="text-3xl font-extrabold text-white">{clamped.toFixed(0)}%</div>
      </div>
      <div className="h-2 bg-slate-700/40 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${clamped}%`, background: c }}
        />
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-700/30 last:border-0">
      <span className="text-slate-400 text-xs">{label}</span>
      <span className="text-slate-200 text-sm font-mono">{value}</span>
    </div>
  );
}

const METRIC_LABEL = {
  invoices:  'Invoices',
  products:  'Products',
  customers: 'Customers',
  movements: 'Movements',
  sales:     'Sales',
};

export function CapacityPanel() {
  const [data, setData] = useState(null);
  const [topMetric, setTopMetric] = useState('invoices');
  const [topRows, setTopRows] = useState([]);
  const [indexHealth, setIndexHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cap, top, idx] = await Promise.all([
        api.get('/superadmin/capacity'),
        api.get('/superadmin/capacity/top-tenants', { params: { metric: topMetric, limit: 10 } }),
        api.get('/superadmin/index-health'),
      ]);
      setData(cap.data);
      setTopRows(top.data?.rows || []);
      setIndexHealth(idx.data);
      setLastUpdated(new Date());
    } catch (e) {
      toast.error('Failed to load capacity stats');
    } finally {
      setLoading(false);
    }
  }, [topMetric]);

  const [tick, setTick] = useState(0);
  const tickRef = useRef(setTick);
  tickRef.current = setTick;

  useEffect(() => { load(); }, [load, tick]);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const fn = () => tickRef.current((n) => n + 1);
    const t = setInterval(fn, 20000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  if (loading && !data) {
    return <div className="text-center py-12 text-slate-500" data-testid="capacity-loading">Loading capacity stats…</div>;
  }
  if (!data) {
    return <div className="text-center py-12 text-slate-500">No capacity data available.</div>;
  }

  const host = data.host || {};
  const mongo = data.mongo || {};
  const rec = data.recommendation || {};
  const meta = STATUS_META[rec.status] || STATUS_META.healthy;
  const RecIcon = meta.icon;

  const conns = mongo.server?.connections || {};
  const connTotal = (conns.current || 0) + (conns.available || 0);
  const connPct = connTotal ? (conns.current / connTotal) * 100 : 0;

  const wt = mongo.server?.wiredtiger_cache_mb || {};
  const wtPct = wt.max ? (wt.current / wt.max) * 100 : 0;

  const fmtMB = (mb) => {
    if (!mb && mb !== 0) return '—';
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6" data-testid="capacity-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-white font-semibold text-lg flex items-center gap-2">
            <Activity size={18} className="text-emerald-400" />
            Capacity Insights
          </h2>
          <p className="text-slate-400 text-xs mt-0.5">
            Host + Mongo load • {lastUpdated ? `updated ${lastUpdated.toLocaleTimeString()}` : 'never'}
            {autoRefresh && <span className="ml-2 text-emerald-400">● auto-refreshing 20s</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setAutoRefresh((v) => !v)}
            data-testid="capacity-toggle-autorefresh"
            className="text-slate-300 hover:text-white"
          >
            {autoRefresh ? 'Pause' : 'Resume'} auto-refresh
          </Button>
          <Button
            size="sm"
            onClick={load}
            data-testid="capacity-refresh-btn"
            className="bg-slate-700 hover:bg-slate-600 text-white"
          >
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Recommendation banner */}
      <div
        className={`rounded-2xl border p-5 flex items-start gap-4 ${
          rec.status === 'upgrade_now'
            ? 'bg-red-500/10 border-red-500/30'
            : rec.status === 'plan_upgrade'
            ? 'bg-amber-500/10 border-amber-500/30'
            : 'bg-emerald-500/10 border-emerald-500/30'
        }`}
        data-testid="capacity-recommendation"
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-${meta.color}-500/20 flex-shrink-0`}>
          <RecIcon size={20} className={`text-${meta.color}-400`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-${meta.color}-300 font-semibold`}>{meta.label}</span>
            <Badge className="bg-slate-700/60 text-slate-300 border-slate-600 text-[10px]">
              {(rec.flags || []).length} flag{(rec.flags || []).length === 1 ? '' : 's'}
            </Badge>
          </div>
          {(rec.flags || []).length === 0 ? (
            <div className="text-slate-300 text-sm">
              All systems are within healthy thresholds. No action needed.
            </div>
          ) : (
            <ul className="space-y-1 mt-1">
              {(rec.flags || []).map((f, i) => (
                <li key={i} className="text-sm flex items-start gap-2">
                  <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                    f.level === 'critical' ? 'bg-red-400' : 'bg-amber-400'
                  }`} />
                  <span className={f.level === 'critical' ? 'text-red-300' : 'text-amber-300'}>
                    {f.message}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3 pt-3 border-t border-slate-700/40 text-xs">
            <div><span className="text-slate-500">RAM </span><span className="font-mono text-slate-200">{rec.summary?.memory_pct?.toFixed(0)}%</span></div>
            <div><span className="text-slate-500">CPU </span><span className="font-mono text-slate-200">{rec.summary?.cpu_pct?.toFixed(0)}%</span></div>
            <div><span className="text-slate-500">Disk </span><span className="font-mono text-slate-200">{rec.summary?.disk_pct?.toFixed(0)}%</span></div>
            <div><span className="text-slate-500">DB conns </span><span className="font-mono text-slate-200">{rec.summary?.mongo_conn_pct?.toFixed(0)}%</span></div>
            <div><span className="text-slate-500">WT cache </span><span className="font-mono text-slate-200">{rec.summary?.wiredtiger_cache_pct?.toFixed(0)}%</span></div>
          </div>
        </div>
      </div>

      {/* Gauges */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Gauge
          label="Host RAM"
          pct={host.memory?.percent}
          sub={`${host.memory?.used_gb?.toFixed(1) || 0} / ${host.memory?.total_gb?.toFixed(1) || 0} GB`}
        />
        <Gauge
          label="CPU"
          pct={host.cpu?.percent}
          sub={`${host.cpu?.cores || 0} cores`}
        />
        <Gauge
          label="Disk"
          pct={host.disk?.percent}
          sub={`${host.disk?.used_gb?.toFixed(1) || 0} / ${host.disk?.total_gb?.toFixed(1) || 0} GB`}
        />
        <Gauge
          label="Mongo conns"
          pct={connPct}
          sub={`${conns.current || 0} / ${connTotal} open`}
        />
      </div>

      {/* Mongo server + DB */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Server size={16} className="text-indigo-400" />
            <h3 className="text-white font-semibold text-sm">Mongo Server</h3>
            <Badge className="bg-slate-700/60 text-slate-400 border-slate-600 text-[10px]">
              v{mongo.server?.version || '—'}
            </Badge>
          </div>
          <div className="space-y-0.5 text-sm">
            <StatRow label="Uptime" value={`${mongo.server?.uptime_hours?.toFixed(1) || 0}h`} />
            <StatRow label="Connections (current / max)" value={`${conns.current || 0} / ${connTotal}`} />
            <StatRow label="Active operations" value={conns.active || 0} />
            <StatRow label="Total queries" value={(mongo.server?.ops_total?.query || 0).toLocaleString()} />
            <StatRow label="Total inserts" value={(mongo.server?.ops_total?.insert || 0).toLocaleString()} />
            <StatRow label="Total updates" value={(mongo.server?.ops_total?.update || 0).toLocaleString()} />
            <StatRow label="Resident memory" value={`${mongo.server?.memory_mb?.resident || 0} MB`} />
            <StatRow label="WiredTiger cache" value={`${wt.current?.toFixed(1) || 0} / ${wt.max?.toFixed(0) || 0} MB (${wtPct.toFixed(0)}%)`} />
            <StatRow label="Net bytes in / out" value={`${fmtMB((mongo.server?.network?.bytes_in || 0) / 1024 / 1024)} / ${fmtMB((mongo.server?.network?.bytes_out || 0) / 1024 / 1024)}`} />
          </div>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Database size={16} className="text-emerald-400" />
            <h3 className="text-white font-semibold text-sm">Database</h3>
            <Badge className="bg-slate-700/60 text-slate-400 border-slate-600 text-[10px]">
              {mongo.db?.name || '—'}
            </Badge>
          </div>
          <div className="space-y-0.5 text-sm">
            <StatRow label="Collections" value={mongo.db?.collections?.toLocaleString() || 0} />
            <StatRow label="Documents" value={mongo.db?.documents?.toLocaleString() || 0} />
            <StatRow label="Data size" value={fmtMB(mongo.db?.data_size_mb)} />
            <StatRow label="Storage size" value={fmtMB(mongo.db?.storage_size_mb)} />
            <StatRow label="Index size" value={fmtMB(mongo.db?.index_size_mb)} />
            <StatRow label="Total on-disk" value={fmtMB(mongo.db?.total_size_mb)} />
            <StatRow
              label="Tenants"
              value={`${mongo.tenants?.total || 0} total · ${mongo.tenants?.active_subscription || 0} active · ${mongo.tenants?.active_last_24h || 0} active 24h`}
            />
          </div>
        </div>
      </div>

      {/* Collection sizes */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Layers size={16} className="text-violet-400" />
          <h3 className="text-white font-semibold text-sm">Hot Collections — Storage & Indexes</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="capacity-collections-table">
            <thead>
              <tr className="text-slate-400 text-xs uppercase border-b border-slate-700/40">
                <th className="text-left py-2 px-2">Collection</th>
                <th className="text-right py-2 px-2">Documents</th>
                <th className="text-right py-2 px-2">Data</th>
                <th className="text-right py-2 px-2">Storage</th>
                <th className="text-right py-2 px-2">Indexes</th>
                <th className="text-right py-2 px-2"># Indexes</th>
                <th className="text-right py-2 px-2">Avg doc</th>
              </tr>
            </thead>
            <tbody>
              {(mongo.collections || []).map((c) => (
                <tr key={c.name} className="border-b border-slate-700/20 hover:bg-slate-700/20">
                  <td className="py-2 px-2 text-slate-200 font-mono">{c.name}</td>
                  <td className="py-2 px-2 text-right text-slate-300">{c.documents?.toLocaleString() || 0}</td>
                  <td className="py-2 px-2 text-right text-slate-400">{fmtMB(c.size_mb)}</td>
                  <td className="py-2 px-2 text-right text-slate-300">{fmtMB(c.storage_mb)}</td>
                  <td className="py-2 px-2 text-right text-slate-400">{fmtMB(c.index_mb)}</td>
                  <td className="py-2 px-2 text-right text-slate-300">{c.indexes}</td>
                  <td className="py-2 px-2 text-right text-slate-500">{c.avg_doc_bytes || 0}B</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top tenants */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp size={16} className="text-amber-400" />
            <h3 className="text-white font-semibold text-sm">Top tenants — by {METRIC_LABEL[topMetric]}</h3>
          </div>
          <div className="flex flex-wrap gap-1">
            {Object.keys(METRIC_LABEL).map((m) => (
              <Button
                key={m}
                size="sm"
                variant={m === topMetric ? 'default' : 'ghost'}
                onClick={() => setTopMetric(m)}
                data-testid={`capacity-top-metric-${m}`}
                className={m === topMetric
                  ? 'bg-indigo-600 hover:bg-indigo-500 text-white text-xs h-7 px-2'
                  : 'text-slate-400 hover:text-white text-xs h-7 px-2'}
              >
                {METRIC_LABEL[m]}
              </Button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="capacity-top-tenants-table">
            <thead>
              <tr className="text-slate-400 text-xs uppercase border-b border-slate-700/40">
                <th className="text-left py-2 px-2">#</th>
                <th className="text-left py-2 px-2">Organization</th>
                <th className="text-left py-2 px-2">Plan</th>
                <th className="text-right py-2 px-2">{METRIC_LABEL[topMetric]}</th>
              </tr>
            </thead>
            <tbody>
              {topRows.length === 0 && (
                <tr><td colSpan={4} className="py-4 text-center text-slate-500">No tenant data yet.</td></tr>
              )}
              {topRows.map((r, i) => (
                <tr key={r.organization_id} className="border-b border-slate-700/20 hover:bg-slate-700/20">
                  <td className="py-2 px-2 text-slate-500">{i + 1}</td>
                  <td className="py-2 px-2 text-slate-200">{r.name}</td>
                  <td className="py-2 px-2">
                    <Badge className="bg-slate-700/60 text-slate-300 border-slate-600 text-[10px] uppercase">
                      {r.plan || '—'}
                    </Badge>
                  </td>
                  <td className="py-2 px-2 text-right text-slate-200 font-mono">
                    {r.count?.toLocaleString() || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Index health */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={16} className="text-yellow-400" />
          <h3 className="text-white font-semibold text-sm">Index Health</h3>
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px]">
            All {(indexHealth?.collections || []).reduce((s, c) => s + (c.org_leading_count || 0), 0)} org-leading indexes
          </Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="capacity-index-health-table">
            <thead>
              <tr className="text-slate-400 text-xs uppercase border-b border-slate-700/40">
                <th className="text-left py-2 px-2">Collection</th>
                <th className="text-right py-2 px-2">Total idx</th>
                <th className="text-right py-2 px-2">Org-leading</th>
                <th className="text-left py-2 px-2">Used (ops)</th>
              </tr>
            </thead>
            <tbody>
              {(indexHealth?.collections || []).map((c) => {
                const orgUsed = (c.indexes || [])
                  .filter((i) => i.org_leading)
                  .reduce((s, i) => s + (i.ops_used || 0), 0);
                return (
                  <tr key={c.collection} className="border-b border-slate-700/20 hover:bg-slate-700/20">
                    <td className="py-2 px-2 text-slate-200 font-mono">{c.collection}</td>
                    <td className="py-2 px-2 text-right text-slate-300">{c.count || 0}</td>
                    <td className="py-2 px-2 text-right">
                      <span className={`font-mono ${c.org_leading_count > 0 ? 'text-emerald-300' : 'text-slate-500'}`}>
                        {c.org_leading_count || 0}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-slate-400">
                      {orgUsed > 0
                        ? <span className="text-emerald-300">{orgUsed.toLocaleString()} hits</span>
                        : <span className="text-slate-500">no hits yet</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Run <code className="bg-slate-700/40 text-slate-300 px-1 rounded">python -m scripts.explain_hot_queries</code> on the server to confirm IXSCAN on all hot queries.
        </p>
      </div>
    </div>
  );
}

export default CapacityPanel;
